export const EPISODE_MODE_ALL = 'all';
export const EPISODE_MODE_SELECTED = 'selected';
export const EPISODE_MODE_NONE = 'none';

export const EPISODE_SELECTION_ACTION_ALL = 'all';
export const EPISODE_SELECTION_ACTION_SEASON = 'season';
export const EPISODE_SELECTION_ACTION_EPISODE = 'episode';

export function normalizeEpisodeIds(ids) {
    return Array.from(new Set((ids || []).map(String)));
}

export function getAvailableEpisodeIds(episodes) {
    return normalizeEpisodeIds((episodes || []).map((episode) => episode?.id).filter((id) => id !== null && id !== undefined));
}

export function getEffectiveSelectedEpisodeIds(selection, availableEpisodeIds) {
    const availableIds = normalizeEpisodeIds(availableEpisodeIds);
    const availableIdSet = new Set(availableIds);

    if (selection?.mode === EPISODE_MODE_ALL) {
        return availableIds;
    }

    if (selection?.mode === EPISODE_MODE_NONE) {
        return [];
    }

    return normalizeEpisodeIds(selection?.selectedEpisodeIds).filter((id) => availableIdSet.has(id));
}

export function normalizeEpisodeSelection(selectedIds, availableEpisodeIds) {
    const availableIds = normalizeEpisodeIds(availableEpisodeIds);
    const availableIdSet = new Set(availableIds);
    const normalizedIds = normalizeEpisodeIds(selectedIds).filter((id) => availableIdSet.has(id));

    if (normalizedIds.length === 0) {
        return { mode: EPISODE_MODE_NONE, selectedEpisodeIds: [] };
    }

    if (availableIds.length > 0 && normalizedIds.length === availableIds.length) {
        return { mode: EPISODE_MODE_ALL, selectedEpisodeIds: [] };
    }

    return { mode: EPISODE_MODE_SELECTED, selectedEpisodeIds: normalizedIds };
}

export function transitionEpisodeSelection(selection, action, availableEpisodeIds) {
    const availableIds = normalizeEpisodeIds(availableEpisodeIds);
    if (availableIds.length === 0) {
        return {
            mode: selection?.mode || EPISODE_MODE_ALL,
            selectedEpisodeIds: normalizeEpisodeIds(selection?.selectedEpisodeIds),
        };
    }

    if (action?.type === EPISODE_SELECTION_ACTION_ALL) {
        return selection?.mode === EPISODE_MODE_ALL
            ? { mode: EPISODE_MODE_NONE, selectedEpisodeIds: [] }
            : { mode: EPISODE_MODE_ALL, selectedEpisodeIds: [] };
    }

    const selectedIds = new Set(getEffectiveSelectedEpisodeIds(selection, availableIds));

    if (action?.type === EPISODE_SELECTION_ACTION_SEASON) {
        const seasonIds = normalizeEpisodeIds(action.episodeIds).filter((id) => availableIds.includes(id));
        const isSeasonChecked = seasonIds.length > 0 && seasonIds.every((id) => selectedIds.has(id));

        seasonIds.forEach((id) => {
            if (isSeasonChecked) {
                selectedIds.delete(id);
            }
            else {
                selectedIds.add(id);
            }
        });

        return normalizeEpisodeSelection(Array.from(selectedIds), availableIds);
    }

    if (action?.type === EPISODE_SELECTION_ACTION_EPISODE) {
        const episodeId = String(action.episodeId);
        if (!availableIds.includes(episodeId)) {
            return normalizeEpisodeSelection(Array.from(selectedIds), availableIds);
        }

        if (selectedIds.has(episodeId)) {
            selectedIds.delete(episodeId);
        }
        else {
            selectedIds.add(episodeId);
        }

        return normalizeEpisodeSelection(Array.from(selectedIds), availableIds);
    }

    return normalizeEpisodeSelection(Array.from(selectedIds), availableIds);
}

export function getEpisodeGroupSelectionState(selection, groupEpisodeIds, availableEpisodeIds) {
    const groupIds = normalizeEpisodeIds(groupEpisodeIds);
    const selectedIdSet = new Set(getEffectiveSelectedEpisodeIds(selection, availableEpisodeIds));
    const selectedCount = groupIds.filter((id) => selectedIdSet.has(id)).length;

    return {
        checked: groupIds.length > 0 && selectedCount === groupIds.length,
        indeterminate: selectedCount > 0 && selectedCount < groupIds.length,
        selectedCount,
        totalCount: groupIds.length,
    };
}

export function getEpisodeSelectionRequest(mode, selectedEpisodeIds) {
    return {
        enabled: mode !== EPISODE_MODE_NONE,
        episodes: mode === EPISODE_MODE_SELECTED
            ? normalizeEpisodeIds(selectedEpisodeIds).join(',')
            : '',
    };
}

export function reconcileEpisodeSelection(selection, availableEpisodeIds) {
    if (selection?.mode === EPISODE_MODE_ALL) {
        return { mode: EPISODE_MODE_ALL, selectedEpisodeIds: [] };
    }

    if (selection?.mode === EPISODE_MODE_NONE) {
        return { mode: EPISODE_MODE_NONE, selectedEpisodeIds: [] };
    }

    return normalizeEpisodeSelection(selection?.selectedEpisodeIds, availableEpisodeIds);
}
